import { useState } from 'react';
import { useOrganizationsList, useOrganizationsCount, useCreateOrganizations, useUpdateOrganizations } from '../api/generated_hooks/generated-organizations-hooks';
import { usePaginatedSearch } from '../hooks/usePaginatedData';
import { OrganizationsRow } from '../types/generated_schemas/generated-organizations-schema';
import { highlightSearchTerms, matchesAllTerms } from '../../../../config/search/search-utils';
import { notify } from '../utils/notifications';
import { tableConfig, getRowStyles, getHeaderStyles } from '../../../../config/ui/tables/table-config';
import { validationMessages, phoneNumberConfig } from '../../../../config/ui/forms/form-config';
import { FormTooltip } from '../components/FormTooltip';
import { OrgDefaultPrizeAdjustmentsSection, type OrgPrizeDraftController } from '../components/organizations/OrgDefaultPrizeAdjustmentsSection';
import { useCreateOrgDefaultPrizeAdjustment, type OrgDefaultPrizeAdjustment } from '../api/extensions/usePrizeOps';
import { FormProgress, useFormProgress } from '../components/FormProgress';
import { Pagination } from '../components/Pagination';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { useFormShake } from '../hooks/useFormShake';
import { useAuthStore } from '../state_management_and_stores/auth.store';
import { useDeleteOrganization } from '../api/extensions/useAdminOps';
import '../../../../config/ui/tables/table-styles.css';
import '../../../../config/ui/forms/form-styles.css';

// The ALL sentinel org — never deletable; also filtered out of admin lists.
const ALL_ORG_ID = '00000000-0000-0000-0000-000000000001';

const emptyForm = {
  name: '',
  short_name: '',
  contact_name: '',
  phone: '',
  website: '',
  membership_url: '',
};

function ManageOrganizationsPage() {
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState<OrganizationsRow | null>(null);
  const [formData, setFormData] = useState({ ...emptyForm });
  // allow_applications is a boolean — kept out of the string-typed formData so
  // the string field validators/handlers don't have to special-case it.
  const [allowApplications, setAllowApplications] = useState(false);
  // organizations.member_fee_enabled — the org-wide default for "does entering
  // our rodeos require membership?". Individual rodeos can override it; this is
  // what they inherit when they don't. Kept out of the string-typed formData
  // for the same reason as allowApplications.
  const [memberFeeEnabled, setMemberFeeEnabled] = useState(true);
  // Create-modal draft for the org-default prize settings. The org doesn't
  // exist yet, so the per-entry contribution and default adjustments are
  // buffered here and persisted after the org insert returns an id (the Edit
  // modal saves these live against an existing org instead).
  const [createPerEntry, setCreatePerEntry] = useState('');
  const [createPerEntryMode, setCreatePerEntryMode] = useState<'amount' | 'percentage' | 'fee_deduction'>('amount');
  const [createDraftAdjustments, setCreateDraftAdjustments] = useState<OrgDefaultPrizeAdjustment[]>([]);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  // Delete-org (system-admin only) — typed-name confirmation.
  const isAllOrgAdmin = useAuthStore((s) => s.isAllOrgAdmin());
  const [deletingOrg, setDeletingOrg] = useState<OrganizationsRow | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const deleteOrganization = useDeleteOrganization();

  const { formRef: createFormRef, shakeInvalidFields } = useFormShake();

  const {
    data: organizationsRaw,
    isLoading,
    error,
    searchTerm,
    setSearchTerm,
    sortBy: sortColumn,
    sortOrder: sortDirection,
    setSortBy: setSortColumn,
    setSortOrder: setSortDirection,
    setCurrentPage,
    setPageSize,
    refetch,
    paginationProps
  } = usePaginatedSearch({
    useListHook: useOrganizationsList as any,
    useCountHook: useOrganizationsCount as any,
    searchFields: ['name', 'short_name', 'contact_name'],
    initialSortBy: 'created_at',
    initialSortOrder: 'desc'
  });

  const organizations = (organizationsRaw ?? []) as OrganizationsRow[];

  const createOrganization = useCreateOrganizations();
  const updateOrganization = useUpdateOrganizations();
  const createOrgDefaultAdjustment = useCreateOrgDefaultPrizeAdjustment();

  // Feeds the shared prize section in draft mode from the create-modal buffers.
  const createPrizeDraft: OrgPrizeDraftController = {
    perEntry: createPerEntry,
    setPerEntry: setCreatePerEntry,
    perEntryMode: createPerEntryMode,
    setPerEntryMode: setCreatePerEntryMode,
    adjustments: createDraftAdjustments,
    setAdjustments: setCreateDraftAdjustments,
  };

  const filteredOrganizations = organizations.filter(org =>
    matchesAllTerms(searchTerm, org, ['name', 'short_name', 'contact_name'], false)
  );

  const formatPhoneNumber = (value: string): string => {
    const phoneNumber = value.replace(/\D/g, '');
    if (phoneNumber.length <= 3) return phoneNumber;
    if (phoneNumber.length <= 6) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    if (phoneNumber.length <= 10) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const validateField = (field: string, value: string): string => {
    if (field === 'name' && !value.trim()) return validationMessages.required;
    if (field === 'phone' && value) {
      const digitsOnly = value.replace(/\D/g, '');
      if (digitsOnly.length > 0 && digitsOnly.length < 10) return 'Phone number is incomplete';
    }
    if (field === 'website' && value) {
      if (!/^https?:\/\/.+/.test(value) && !/^[a-zA-Z0-9]/.test(value)) {
        return 'Enter a valid website URL';
      }
    }
    if (field === 'membership_url' && value) {
      if (!/^https?:\/\/.+/.test(value) && !/^[a-zA-Z0-9]/.test(value)) {
        return 'Enter a valid signup URL';
      }
    }
    return '';
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    ['name', 'phone', 'website', 'membership_url'].forEach(field => {
      const error = validateField(field, formData[field as keyof typeof formData]);
      if (error) errors[field] = error;
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFieldChange = (field: string, value: string) => {
    let actualValue = value;
    if (field === 'phone') {
      const digitsOnly = value.replace(/\D/g, '');
      if (digitsOnly.length <= 10) {
        actualValue = formatPhoneNumber(value);
        setFormData(prev => ({ ...prev, [field]: actualValue }));
      } else {
        return;
      }
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }

    if (touchedFields.has(field)) {
      const error = validateField(field, actualValue);
      setFormErrors(prev => {
        const newErrors = { ...prev };
        if (error) newErrors[field] = error;
        else delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleFieldBlur = (field: string) => {
    setFocusedField(null);
    setTouchedFields(prev => new Set(prev).add(field));
    const error = validateField(field, formData[field as keyof typeof formData]);
    setFormErrors(prev => {
      const newErrors = { ...prev };
      if (error) newErrors[field] = error;
      else delete newErrors[field];
      return newErrors;
    });
  };

  const resetForm = () => {
    setFormData({ ...emptyForm });
    setAllowApplications(false);
    setMemberFeeEnabled(true);
    setCreatePerEntry('');
    setCreatePerEntryMode('amount');
    setCreateDraftAdjustments([]);
    setFormErrors({});
    setTouchedFields(new Set());
    setFocusedField(null);
  };

  const handleCreateOrganization = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      shakeInvalidFields(formErrors);
      notify.error('Please fix the form errors');
      return;
    }

    try {
      const created = await createOrganization.mutateAsync({
        name: formData.name,
        short_name: formData.short_name || null,
        contact_name: formData.contact_name || null,
        phone: formData.phone || null,
        website: formData.website || null,
        membership_url: formData.membership_url || null,
        // Required by the generated POST /organizations insert schema (NOT NULL
        // column). Defaults to true (entering this org's rodeos requires
        // membership); the toggle below turns it off for orgs that run open
        // rodeos. Individual rodeos can still override it either way.
        member_fee_enabled: memberFeeEnabled,
        // Whether this org shows up in the profile Organization Memberships
        // tab so users can claim a member number. Off by default.
        allow_applications: allowApplications,
        // NOT NULL columns from the dynamic-prize feature. These now carry the
        // admin's choice in the create modal's "Default per-entry prize
        // contribution" control (blank → 0, matching the DB defaults).
        default_entry_fee_prize_portion: createPerEntry ? Number(createPerEntry) || 0 : 0,
        default_entry_fee_prize_portion_is_percentage: createPerEntryMode === 'percentage',
        default_entry_fee_prize_portion_is_fee_deduction: createPerEntryMode === 'fee_deduction',
      } as any);

      // Persist any default prize adjustments the admin buffered while the org
      // didn't yet exist. The org row is already created, so a failure here is
      // non-fatal: report it and let them finish in the edit modal rather than
      // rolling back a successful create.
      let adjustmentsFailed = 0;
      for (const adj of createDraftAdjustments) {
        try {
          await createOrgDefaultAdjustment.mutateAsync({
            organization_id: created.id,
            adjustment_type: adj.adjustment_type,
            amount: adj.amount,
            percentage: adj.percentage,
            reason: adj.reason,
            is_active: adj.is_active,
            sort_order: adj.sort_order,
          });
        } catch {
          adjustmentsFailed += 1;
        }
      }

      if (adjustmentsFailed > 0) {
        notify.warning(
          `Organization ${formData.name} created, but ${adjustmentsFailed} default prize adjustment${adjustmentsFailed === 1 ? '' : 's'} could not be saved. Add ${adjustmentsFailed === 1 ? 'it' : 'them'} from the edit screen.`,
        );
      } else {
        notify.success(`Organization ${formData.name} has been created successfully`);
      }
      setShowCreateModal(false);
      resetForm();
      refetch();
    } catch (error: any) {
      notify.error(`Failed to create organization: ${error?.message || 'Unknown error'}`);
    }
  };

  const handleEditClick = (org: OrganizationsRow) => {
    setEditingOrg(org);
    setFormData({
      name: (org as any).name || '',
      short_name: (org as any).short_name || '',
      contact_name: (org as any).contact_name || '',
      phone: (org as any).phone || '',
      website: (org as any).website || '',
      membership_url: (org as any).membership_url || '',
    });
    setAllowApplications(!!(org as any).allow_applications);
    // NOT NULL DEFAULT true on the column — only an explicit false turns it off.
    setMemberFeeEnabled((org as any).member_fee_enabled !== false);
    setFormErrors({});
    setTouchedFields(new Set());
    setShowEditModal(true);
  };

  const handleUpdateOrganization = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !editingOrg) return;

    try {
      await updateOrganization.mutateAsync({
        id: editingOrg.id,
        data: {
          name: formData.name,
          short_name: formData.short_name || null,
          contact_name: formData.contact_name || null,
          phone: formData.phone || null,
          website: formData.website || null,
          membership_url: formData.membership_url || null,
          allow_applications: allowApplications,
          member_fee_enabled: memberFeeEnabled,
        } as any
      });
      notify.success(`Organization ${formData.name} has been updated successfully`);
      setShowEditModal(false);
      setEditingOrg(null);
      resetForm();
      refetch();
    } catch (error: any) {
      notify.error(`Failed to update organization: ${error?.message || 'Unknown error'}`);
    }
  };

  const openDeleteModal = (org: OrganizationsRow) => {
    setDeletingOrg(org);
    setDeleteConfirmText('');
  };
  const closeDeleteModal = () => {
    setDeletingOrg(null);
    setDeleteConfirmText('');
  };
  const deleteNameMatches =
    !!deletingOrg &&
    deleteConfirmText.trim() === String((deletingOrg as any).name ?? '').trim() &&
    deleteConfirmText.trim().length > 0;

  const handleConfirmDelete = async () => {
    if (!deletingOrg || !deleteNameMatches) return;
    try {
      await deleteOrganization.mutateAsync({
        organizationId: deletingOrg.id,
        confirmName: deleteConfirmText.trim(),
      });
      notify.success(`Organization "${(deletingOrg as any).name}" deleted`);
      closeDeleteModal();
      refetch();
    } catch (error: any) {
      notify.error(error?.message || 'Failed to delete organization');
    }
  };

  const requiredFields = ['name'];
  const optionalFields = ['short_name', 'contact_name', 'phone', 'website', 'membership_url'];
  const progress = useFormProgress(formData, formErrors, requiredFields, optionalFields);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-destructive/10 border border-destructive/30 rounded-md p-4">
        <h3 className="text-destructive font-medium">Error loading organizations</h3>
        <p className="text-destructive/80 mt-1">{error.message}</p>
        <button onClick={() => refetch()} className="mt-2 px-4 py-2 bg-destructive text-destructive-foreground rounded hover:bg-destructive/90">Retry</button>
      </div>
    );
  }

  // Opt-in switch that surfaces this org in the profile "Organization
  // Memberships" tab. Extracted so the edit modal can render it in the right
  // column (alongside Default Prize Adjustments) while the create modal keeps
  // it inline in the single-column form.
  const renderAllowApplications = (isEdit: boolean) => (
    <div className="app-form-field">
      <div className="flex items-start justify-between gap-4 rounded-md border border-border p-3">
        <div className="min-w-0">
          <label
            htmlFor={`${isEdit ? 'edit' : 'create'}_org_allow_applications`}
            className="app-form-label app-form-label--optional"
          >
            Accept membership applications
          </label>
          <p className="app-form-help">
            When on, this organization appears in every user's profile
            Organization Memberships tab so they can submit or view a
            membership number. Leave off for test or one-off organizations.
          </p>
        </div>
        <Switch
          id={`${isEdit ? 'edit' : 'create'}_org_allow_applications`}
          checked={allowApplications}
          onCheckedChange={setAllowApplications}
          className="mt-1 shrink-0"
        />
      </div>
    </div>
  );

  // Org-wide default for whether entering this organization's rodeos requires
  // membership. Rendered alongside the applications switch so the two
  // membership-related org settings sit together. Individual rodeos can
  // override this from the rodeo wizard.
  const renderMemberFeeEnabled = (isEdit: boolean) => (
    <div className="app-form-field">
      <div className="flex items-start justify-between gap-4 rounded-md border border-border p-3">
        <div className="min-w-0">
          <label
            htmlFor={`${isEdit ? 'edit' : 'create'}_org_member_fee_enabled`}
            className="app-form-label app-form-label--optional"
          >
            Require membership to enter
          </label>
          <p className="app-form-help">
            Default for this organization's rodeos. When off, everyone enters
            as a member: no non-member surcharge, no membership prompt at
            entry, and Cash / Pay Later are open to all contestants. Any
            individual rodeo can override this in the rodeo wizard.
          </p>
        </div>
        <Switch
          id={`${isEdit ? 'edit' : 'create'}_org_member_fee_enabled`}
          checked={memberFeeEnabled}
          onCheckedChange={setMemberFeeEnabled}
          className="mt-1 shrink-0"
        />
      </div>
    </div>
  );

  const renderOrgForm = (onSubmit: (e: React.FormEvent) => void, submitLabel: string, isPending: boolean, isEdit: boolean) => (
    <form ref={isEdit ? undefined : createFormRef} onSubmit={onSubmit} className="p-6">
      <div className="mb-4">
        <FormProgress formData={formData} errors={formErrors} requiredFields={requiredFields} optionalFields={optionalFields} />
      </div>

      <div className="space-y-4">
        {/* Name */}
        <div className="app-form-field">
          <label htmlFor={`${isEdit ? 'edit' : 'create'}_org_name`} className="app-form-label app-form-label--required">
            Organization Name
            {focusedField === 'name' && <FormTooltip content="The official name of the organization" />}
          </label>
          <Input
            id={`${isEdit ? 'edit' : 'create'}_org_name`}
            className={formErrors.name ? 'border-destructive focus-visible:ring-destructive' : ''}
            value={formData.name}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            onFocus={() => setFocusedField('name')}
            onBlur={() => handleFieldBlur('name')}
            placeholder="e.g., Texas Team Ropers Association"
          />
          {formErrors.name && <div className="app-form-help app-form-help--error">{formErrors.name}</div>}
        </div>

        {/* Short Name */}
        <div className="app-form-field">
          <label htmlFor={`${isEdit ? 'edit' : 'create'}_org_short_name`} className="app-form-label app-form-label--optional">
            Short Name / Abbreviation
            {focusedField === 'short_name' && <FormTooltip content="A short abbreviation or nickname for the organization" />}
          </label>
          <Input
            id={`${isEdit ? 'edit' : 'create'}_org_short_name`}
            value={formData.short_name}
            onChange={(e) => handleFieldChange('short_name', e.target.value)}
            onFocus={() => setFocusedField('short_name')}
            onBlur={() => handleFieldBlur('short_name')}
            placeholder="e.g., TTRA"
          />
        </div>

        {/* Contact Name */}
        <div className="app-form-field">
          <label htmlFor={`${isEdit ? 'edit' : 'create'}_org_contact`} className="app-form-label app-form-label--optional">
            Contact Name
            {focusedField === 'contact_name' && <FormTooltip content="Primary contact person for this organization" />}
          </label>
          <Input
            id={`${isEdit ? 'edit' : 'create'}_org_contact`}
            value={formData.contact_name}
            onChange={(e) => handleFieldChange('contact_name', e.target.value)}
            onFocus={() => setFocusedField('contact_name')}
            onBlur={() => handleFieldBlur('contact_name')}
            placeholder="e.g., John Smith"
          />
        </div>

        {/* Phone */}
        <div className="app-form-field">
          <label htmlFor={`${isEdit ? 'edit' : 'create'}_org_phone`} className="app-form-label app-form-label--optional">
            Phone
            {focusedField === 'phone' && <FormTooltip content="Organization phone number" />}
          </label>
          <Input
            id={`${isEdit ? 'edit' : 'create'}_org_phone`}
            type="tel"
            className={formErrors.phone ? 'border-destructive focus-visible:ring-destructive' : ''}
            value={formData.phone}
            onChange={(e) => handleFieldChange('phone', e.target.value)}
            onFocus={() => setFocusedField('phone')}
            onBlur={() => handleFieldBlur('phone')}
            placeholder={phoneNumberConfig.placeholder}
          />
          {formErrors.phone && <div className="app-form-help app-form-help--error">{formErrors.phone}</div>}
          {!formErrors.phone && <div className="app-form-help">US phone format: {phoneNumberConfig.format}</div>}
        </div>

        {/* Website */}
        <div className="app-form-field">
          <label htmlFor={`${isEdit ? 'edit' : 'create'}_org_website`} className="app-form-label app-form-label--optional">
            Website URL
            {focusedField === 'website' && <FormTooltip content="The organization's general homepage. Used as a fallback for the 'Become a Member' link when no Direct Signup Link is set." />}
          </label>
          <Input
            id={`${isEdit ? 'edit' : 'create'}_org_website`}
            type="url"
            className={formErrors.website ? 'border-destructive focus-visible:ring-destructive' : ''}
            value={formData.website}
            onChange={(e) => handleFieldChange('website', e.target.value)}
            onFocus={() => setFocusedField('website')}
            onBlur={() => handleFieldBlur('website')}
            placeholder="https://example.com"
          />
          {formErrors.website && <div className="app-form-help app-form-help--error">{formErrors.website}</div>}
        </div>

        {/* Direct Signup Link */}
        <div className="app-form-field">
          <label htmlFor={`${isEdit ? 'edit' : 'create'}_org_membership_url`} className="app-form-label app-form-label--optional">
            Direct Signup Link
            {focusedField === 'membership_url' && <FormTooltip content="Direct URL to the organization's membership signup page. When set, the 'Become a Member' button on event entry sends users here. Falls back to the Website URL if left blank." />}
          </label>
          <Input
            id={`${isEdit ? 'edit' : 'create'}_org_membership_url`}
            type="url"
            className={formErrors.membership_url ? 'border-destructive focus-visible:ring-destructive' : ''}
            value={formData.membership_url}
            onChange={(e) => handleFieldChange('membership_url', e.target.value)}
            onFocus={() => setFocusedField('membership_url')}
            onBlur={() => handleFieldBlur('membership_url')}
            placeholder="https://example.com/join"
          />
          {formErrors.membership_url ? (
            <div className="app-form-help app-form-help--error">{formErrors.membership_url}</div>
          ) : (
            <div className="app-form-help">If blank, the Website URL above is used for the membership signup link.</div>
          )}
        </div>

        {/* Both modals render the membership switch in the right column
            (alongside the default prize settings), so it's omitted from the
            single-column profile form here to avoid duplication. */}
      </div>

      <div className="mt-6 flex justify-end space-x-3">
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            if (isEdit) {
              setShowEditModal(false);
              setEditingOrg(null);
            } else {
              setShowCreateModal(false);
            }
            resetForm();
          }}
          disabled={isPending}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isPending || !progress.isSubmittable}
        >
          {isPending ? (isEdit ? 'Updating...' : 'Creating...') : submitLabel}
        </Button>
      </div>
    </form>
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Manage Organizations</h1>
            <p className="mt-1 text-sm text-muted-foreground">View and manage all organizations in the system.</p>
          </div>
          <Button onClick={() => setShowCreateModal(true)}>
            + Add New Organization
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label htmlFor="search" className="sr-only">Search organizations</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <Input
                id="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-9"
                placeholder="Search by name, short name, or contact..."
              />
            </div>
          </div>
        </div>
      </div>

      {/* Organizations Table */}
      <div className="rounded-lg border bg-card shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="app-table app-table--interactive" style={tableConfig.baseStyle}>
            <thead>
              <tr>
                <th
                  className="app-table-header app-table-header--sortable"
                  style={getHeaderStyles(false, sortColumn === 'name', true)}
                  onClick={() => {
                    setSortColumn('name');
                    setSortDirection(sortColumn === 'name' ? (sortDirection === 'asc' ? 'desc' : 'asc') : 'asc');
                  }}
                >
                  Organization
                </th>
                <th className="app-table-header">Short Name</th>
                <th className="app-table-header">Contact</th>
                <th className="app-table-header">Phone</th>
                <th className="app-table-header">Website</th>
                <th
                  className={`app-table-header app-table-header--sortable ${sortColumn === 'created_at' ? `app-table-header--sorted ${sortDirection}` : ''}`}
                  style={getHeaderStyles(false, sortColumn === 'created_at', true)}
                  onClick={() => {
                    setSortColumn('created_at');
                    setSortDirection(sortColumn === 'created_at' ? (sortDirection === 'asc' ? 'desc' : 'asc') : 'asc');
                  }}
                >
                  Created
                </th>
                <th className="app-table-header">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredOrganizations.map((org, index) => (
                <tr
                  key={org.id}
                  className="app-table-row"
                  style={getRowStyles(hoveredRow === index, selectedRow === index, false, index % 2 === 0)}
                  onMouseEnter={() => setHoveredRow(index)}
                  onMouseLeave={() => setHoveredRow(null)}
                  onClick={() => setSelectedRow(index)}
                >
                  <td className="app-table-cell">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full flex items-center justify-center bg-accent text-accent-foreground">
                          <span className="text-sm font-medium">
                            {((org as any).short_name || (org as any).name || 'OR').substring(0, 2).toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-card-foreground">
                          {highlightSearchTerms((org as any).name, searchTerm)}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="app-table-cell text-sm text-muted-foreground">
                    {(org as any).short_name ? highlightSearchTerms((org as any).short_name, searchTerm) : <span className="text-muted-foreground/50">{'\u2014'}</span>}
                  </td>
                  <td className="app-table-cell text-sm text-muted-foreground">
                    {(org as any).contact_name ? highlightSearchTerms((org as any).contact_name, searchTerm) : <span className="text-muted-foreground/50">{'\u2014'}</span>}
                  </td>
                  <td className="app-table-cell text-sm text-muted-foreground">
                    {(org as any).phone || <span className="text-muted-foreground/50">{'\u2014'}</span>}
                  </td>
                  <td className="app-table-cell text-sm text-muted-foreground">
                    {(org as any).website ? (
                      <a href={(org as any).website.startsWith('http') ? (org as any).website : `https://${(org as any).website}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate max-w-[200px] inline-block">
                        {(org as any).website.replace(/^https?:\/\//, '')}
                      </a>
                    ) : <span className="text-muted-foreground/50">{'\u2014'}</span>}
                  </td>
                  <td className="app-table-cell text-sm text-muted-foreground">
                    {org.created_at ? new Date(org.created_at as string).toLocaleDateString() : '\u2014'}
                  </td>
                  <td className="app-table-cell text-right text-sm font-medium">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleEditClick(org); }}
                        className="p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-accent transition-colors"
                        aria-label={`Edit ${(org as any).name}`}
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                      {/* Delete — system (ALL-org) admins only; never the ALL sentinel. */}
                      {isAllOrgAdmin && org.id !== ALL_ORG_ID && (
                        <button
                          onClick={(e) => { e.stopPropagation(); openDeleteModal(org); }}
                          className="p-1.5 rounded-md text-muted-foreground hover:text-red-500 hover:bg-accent transition-colors"
                          aria-label={`Delete ${(org as any).name}`}
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-3 border-t border-border">
          <Pagination
            {...paginationProps}
            onPageChange={(page) => { setCurrentPage(page); setSelectedRow(null); }}
            onPageSizeChange={(newSize) => { setPageSize(newSize); setSelectedRow(null); }}
            variant="default"
            showPageSizeSelector={true}
            showItemCount={true}
            showJumpTo={false}
            className="justify-center sm:justify-between"
          />
        </div>
      </div>

      {/* Create Organization Modal — mirrors the Edit modal's two-column
          layout: org profile on the left, org-default prize settings on the
          right (buffered in draft state until the org is created). */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => { setShowCreateModal(false); resetForm(); }} />
            <div className="relative z-50 flex max-h-[90vh] w-full max-w-6xl flex-col rounded-lg border bg-card text-card-foreground shadow-xl">
              <div className="p-6 border-b border-border shrink-0">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold">Create New Organization</h2>
                  <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="text-muted-foreground hover:text-foreground transition-colors" aria-label="Close">
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              </div>
              <div className="overflow-y-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 lg:divide-x divide-border">
                  <div>
                    {renderOrgForm(handleCreateOrganization, 'Create Organization', createOrganization.isPending, false)}
                  </div>
                  {/* Right column: membership-applications switch (persisted with
                      the create form's submit) above the org-level default prize
                      settings (buffered, then persisted after the org insert). */}
                  <div className="p-6 space-y-4">
                    {renderMemberFeeEnabled(false)}
                    {renderAllowApplications(false)}
                    <OrgDefaultPrizeAdjustmentsSection draftController={createPrizeDraft} />
                  </div>
                </div>
              </div>
            </div>
        </div>
      )}

      {/* Edit Organization Modal */}
      {showEditModal && editingOrg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => { setShowEditModal(false); setEditingOrg(null); resetForm(); }} />
            <div className="relative z-50 flex max-h-[90vh] w-full max-w-6xl flex-col rounded-lg border bg-card text-card-foreground shadow-xl">
              <div className="p-6 border-b border-border shrink-0">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold">Edit Organization</h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Editing: <span className="font-semibold text-primary">{(editingOrg as any).name}</span>
                    </p>
                  </div>
                  <button onClick={() => { setShowEditModal(false); setEditingOrg(null); resetForm(); }} className="text-muted-foreground hover:text-foreground transition-colors" aria-label="Close">
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              </div>
              {/* Two-column body: org profile on the left, default prize
                  adjustments on the right. The body scrolls within the viewport
                  (modal capped at 90vh) so a long adjustments list never pushes
                  the modal off-screen. Collapses to a single column below lg. */}
              <div className="overflow-y-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 lg:divide-x divide-border">
                  <div>
                    {renderOrgForm(handleUpdateOrganization, 'Update Organization', updateOrganization.isPending, true)}
                  </div>
                  {/* Right column: membership-applications switch (saved with
                      the profile form's Update button) above the org-level
                      default prize adjustments (managed independently via
                      prize-ops). */}
                  <div className="p-6 space-y-4">
                    {renderMemberFeeEnabled(true)}
                    {renderAllowApplications(true)}
                    <OrgDefaultPrizeAdjustmentsSection organizationId={editingOrg.id} />
                  </div>
                </div>
              </div>
            </div>
        </div>
      )}

      {/* Delete Organization — strong confirmation (type the org name) */}
      {deletingOrg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/60 transition-opacity" onClick={closeDeleteModal} />
          <div className="relative z-50 w-full max-w-lg rounded-lg border border-red-500/40 bg-card text-card-foreground shadow-xl">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-semibold text-red-500">Delete Organization</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                You are about to permanently delete{' '}
                <span className="font-semibold text-foreground">{(deletingOrg as any).name}</span>.
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div className="rounded-md border border-red-500/30 bg-red-500/5 p-3 text-sm text-muted-foreground">
                This cannot be undone. Members lose their roles in this org, its membership
                roster and priority list are removed, and any membership cards tied to it are
                detached. It will fail if the org still has rodeos (delete or reassign those first).
              </div>
              <div>
                <label htmlFor="delete_org_confirm" className="block text-sm font-medium mb-1">
                  Type <span className="font-mono font-semibold text-foreground">{(deletingOrg as any).name}</span> to confirm
                </label>
                <Input
                  id="delete_org_confirm"
                  autoFocus
                  autoComplete="off"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={(deletingOrg as any).name}
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
              <Button variant="outline" onClick={closeDeleteModal} disabled={deleteOrganization.isPending}>
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDelete}
                disabled={!deleteNameMatches || deleteOrganization.isPending}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {deleteOrganization.isPending ? 'Deleting...' : 'Delete Organization'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageOrganizationsPage;
